// =============================================================================
// client/src/pages/campaigns/CampaignList.jsx | v3.0 | SQ-Chrome S1 | P9a — W-9
// =============================================================================
// The operator's campaign list: a W-4 stat strip, the active cards, and the
// archived list with bulk unarchive.
//
// 🔴 THIS PAGE COMPUTES NO NUMBER, NO STATUS AND NO ORDER IT WAS NOT GIVEN.
// Every stat is W-4's or `shapeStats`'s, rendered EXACTLY as the server
// formatted it — including the "%" strings, which arrive pre-formatted so the
// browser cannot round them a second time and disagree with the report page.
// Every status word, next action and ETA is W-1's `describeCampaign`. The only
// ordering is `status_display.sort_rank`, which is the server's, applied by
// status.js's comparator.
//
// A client that reads pause_reason to decide anything is the RecruitMail defect
// this phase exists to delete: RM decoded state in the browser, in two copies,
// and they drifted.
//
// The ONE thing this page computes is a QUERY BOUNDARY — "today 00:00 ET" and
// the N-day windows — because those are inputs to the server, not outputs from
// it. Named here so the exception is visible rather than assumed.
//
// ── SQ-Chrome S1 · PRESENTATION PASS. NO DATA LOGIC WAS TOUCHED. ────────────
// Every fetch, every state hook, every handler and every string that comes off
// the server is byte-identical to v2.0. What changed is chrome:
//   · FluxEngine now reads as its own product, in the board language Jake
//     specified — 2px ink outlines, a zero-blur `0 4px 0` ledge shadow, 2px
//     DASHED dividers, a 14px left spine on cards, uppercase pill buttons, and
//     a paper ground. It is deliberately NOT the Front Office's soft dashboard.
//   · Campaign transport moved to the BOTTOM of the page (Jake: "it's an
//     emergency switch, it deserves no prominence").
//   · Lifetime is now an actual dashboard — stat tiles with big numerals —
//     rather than an inline sentence.
//   · Active campaigns render 1×2 in a grid, at reduced weight.
//   · A route back to the Front Office exists for the first time. Every
//     FluxEngine surface previously had no way home.
// Colours are arbitrary Tailwind values ON PURPOSE: tailwind.config.js does not
// hot-reload in this setup and every config change needs a server restart,
// which makes colour iteration unusable. Arbitrary values compile on save.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { severityClass, bySortRankThenRecruit } from '../../lib/flux/status.js';
import ConnectGmail from '../../components/flux/ConnectGmail.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import Toast from '../../components/Toast.jsx';
// W-12: the re-auth button now actually sends. `can` gates it on the same
// capability the server enforces (oauth.invite), so an operator who would get a
// 403 never sees the control — §1.1's hide-what-the-server-refuses law.
import { can } from '../../lib/can.js';
import { useMe } from '../../lib/flux/useMe.js';

const PAGE_SIZE = 100;
const RANGES = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  // S95 X-6: was 'All'. The tile band special-cased this id to the word
  // 'Lifetime', so the pill the operator pressed and the label above the
  // numerals named the same period in two different words, both on screen at
  // once. One period, one word, one source — see `shownLabel` below.
  { id: 'all', label: 'Lifetime', days: null },
];

// ── Board vocabulary ────────────────────────────────────────────────────────
// One place, so twenty elements cannot drift apart. Ink is hunter-deep; the
// ledge shadow is zero-blur by design — it is a printed offset, not a glow.
const INK = '#0E2A20';
const PANEL = 'bg-white border-2 border-[#0E2A20] rounded-2xl shadow-[0_4px_0_#0E2A20]';
const PANEL_QUIET = 'bg-white border-2 border-[#0E2A20] rounded-2xl';
const DASH = 'border-t-2 border-dashed border-[#0E2A20]';
// inline-flex + leading-none is what actually centres uppercase tracked text
// in a pill; py alone leaves it riding high because the line-box is taller
// than the caps.
const BTN = 'inline-flex items-center justify-center leading-none text-xs font-semibold uppercase tracking-[0.12em] px-5 py-3 rounded-full border-2 border-[#0E2A20] transition-transform active:translate-y-[2px]';
const BTN_LIGHT = `${BTN} bg-white text-[#0E2A20] shadow-[0_4px_0_#0E2A20] active:shadow-[0_2px_0_#0E2A20]`;
const BTN_DARK = `${BTN} bg-[#0E2A20] text-white shadow-[0_4px_0_#0E2A20] active:shadow-[0_2px_0_#0E2A20]`;

// CCF's day is ET, not the browser's zone. Derived from the ACTUAL ET wall
// clock rather than a hardcoded -5/-4, so it is right on both sides of a DST
// boundary: subtract the elapsed ET time-of-day from now and you have the
// instant of ET midnight, whatever the offset happens to be today.
function etMidnightIso(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const at = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
  const sinceMidnightMs = ((at('hour') * 3600) + (at('minute') * 60) + at('second')) * 1000;
  return new Date(now.getTime() - sinceMidnightMs).toISOString();
}

function daysAgoIso(days) {
  return new Date(Date.now() - (days * 24 * 60 * 60 * 1000)).toISOString();
}

// The server sends `{days}` or `{hours}` or null (flux-status.js computeEta).
// RM's copy, :184-187.
function etaText(eta) {
  if (!eta) return null;
  if (Number.isInteger(eta.days)) return `Estimated: ~${eta.days} Day${eta.days === 1 ? '' : 's'} Remaining`;
  if (Number.isInteger(eta.hours)) return `Estimated: ~${eta.hours} Hour${eta.hours === 1 ? '' : 's'} Remaining`;
  return null;
}

// 🔴 THE SPARKLINES BELOW ARE DECORATIVE. THEY ARE NOT DATA.
// Jake asked for graph lines behind the headline stats and said "true is
// better, fake is fine." True would require a per-day time-series endpoint
// that does not exist, and adding server surface inside a restyle is not what
// this pass is. So these are fixed ornamental paths — they do not vary with
// the numbers above them and they must never be read as a trend.
// If a real series ever ships, replace SPARKS wholesale; do not "correct" it.
//
// 🔴 AND THEY MUST NOT TREND. Revised at SQ-Chrome S1 after CC caught it:
// the first version of these paths every ended at a lower y than it started,
// which in SVG is UP on screen. All four tiles rendered a rising curve —
// including "Bounced 0" (an alarm that was not real) and "Clicked 0 (0.0%)"
// (a climb behind a literal zero). "Fake is fine" licensed ORNAMENT. It did
// not license the screen asserting a direction the server never reported.
// Every path below now RETURNS TO ITS STARTING y: it wanders, it reads as
// texture, it claims nothing. A tile whose value is 0 gets FLAT instead —
// flat behind zero is the one shape that is actually true.
// If you add a path here, start y and end y must match. That is the rule.
const SPARKS = [
  '0,18 14,11 28,20 42,13 56,21 70,12 84,19 98,18',
  '0,14 14,20 28,12 42,19 56,13 70,21 84,15 98,14',
  '0,20 14,13 28,19 42,11 56,18 70,14 84,21 98,20',
  '0,16 14,22 28,14 42,20 56,12 70,19 84,13 98,16',
];
const SPARK_FLAT = '0,17 14,17 28,17 42,17 56,17 70,17 84,17 98,17';

// A dashboard tile. The numeral is the object; the label is annotation.
// `sub` carries the pre-formatted rate string from the server UNCHANGED.
function Tile({ label, value, sub, spark = 0 }) {
  return (
    <div className="relative px-5 pt-5 pb-6 flex-1 min-w-[7.5rem] flex flex-col items-center justify-start text-center overflow-hidden">
      {/* decorative only — see SPARKS above */}
      <svg
        viewBox="0 0 98 30"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-4 bottom-4 top-8 w-auto opacity-[0.13]"
      >
        <polyline
          points={Number(value) === 0 ? SPARK_FLAT : SPARKS[spark % SPARKS.length]}
          fill="none"
          stroke="#0E2A20"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="relative text-[0.69rem] font-semibold uppercase tracking-[0.18em] text-[#5A5A5A]">{label}</div>
      <div className="relative font-display font-normal text-[#0E2A20] text-[2.59rem] leading-none mt-2 tabular-nums">{value}</div>
      {sub ? <div className="relative text-[0.86rem] text-[#5A5A5A] mt-1.5 tabular-nums">{sub}</div> : null}
    </div>
  );
}

export default function CampaignList() {
  const navigate = useNavigate();

  const [lifetime, setLifetime] = useState(null);
  const [today, setToday] = useState(null);
  const [ranged, setRanged] = useState(null);
  const [rangeId, setRangeId] = useState('30');
  const [stripLoading, setStripLoading] = useState(true);

  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [operator, setOperator] = useState('');
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [archived, setArchived] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [reauthFor, setReauthFor] = useState(null);   // campaign id with the connect panel open
  const [reauthSending, setReauthSending] = useState(null); // campaign id mid-send
  const { me } = useMe();
  const canInvite = can(me, 'oauth.invite');
  const isAdmin = me?.team?.role === 'admin';
  const [transport, setTransport] = useState(null);
  const [transportReason, setTransportReason] = useState('');
  const [transportBusy, setTransportBusy] = useState(false);

  const loadTransport = useCallback(() => {
    if (!isAdmin) return;
    api.get('/api/flux/campaigns/admin/transport-gate')
      .then(setTransport)
      .catch((err) => setToast({ tone: 'error', message: err.body?.error || err.message }));
  }, [isAdmin]);

  async function changeTransport(stopped) {
    if (!transport?.valid || !Number.isInteger(Number(transport.version)) || !transportReason.trim()) return;
    setTransportBusy(true);
    try {
      await api.post(`/api/flux/campaigns/admin/transport-gate/${stopped ? 'stop' : 'resume'}`, {
        expectedVersion: Number(transport.version), reason: transportReason.trim(),
      });
      setTransportReason(''); loadTransport();
    } catch (err) { setToast({ tone: 'error', message: err.body?.error || err.message }); }
    finally { setTransportBusy(false); }
  }

  // W-12. The button said "Send Re-Auth Email" and sent nothing — it only
  // toggled the connect panel open. POST /api/flux/oauth/send-invite has
  // shipped since P9b (flux-oauth.js:314) and OutreachTab.jsx:183 has been
  // calling it all along; this card simply never caught up.
  //
  // ⚠ The mailbox is the campaign's OWN send_from_email, not the recruit's
  // record email. The invite link is BOUND to the address it is minted for
  // (the server refuses a missing one outright), and re-authorizing anything
  // other than the mailbox this campaign sends from would mint a credential
  // for the wrong inbox — which is defect D, in a different file.
  async function sendReauth(c) {
    setReauthSending(c.id);
    try {
      const body = await api.post('/api/flux/oauth/send-invite', {
        recruit_id: c.recruit_id,
        gmail_address: c.send_from_email,
      });
      // The server's own `sent_to` is rendered, never the address we sent it —
      // it normalizes and lowercases, and echoing our input would hide a
      // mismatch between what we asked for and what actually got mailed.
      setToast({ tone: 'success', message: `Re-auth email sent to ${body?.sent_to || c.send_from_email}.` });
    } catch (err) {
      // A 429 carries the wait in its own message and a 409 names the bound
      // mailbox. Both are rendered as the server wrote them.
      setToast({ tone: 'error', message: err.body?.error || err.message });
    } finally {
      setReauthSending(null);
    }
  }

  // ── the strip: THREE W-4 calls (§6 W-4 — lifetime, today, picked) ─────────
  const loadStrip = useCallback(() => {
    setStripLoading(true);
    const range = RANGES.find((r) => r.id === rangeId);
    const calls = [
      api.get('/api/flux/campaigns/admin/summary'),
      api.get(`/api/flux/campaigns/admin/summary?dateFrom=${encodeURIComponent(etMidnightIso())}`),
      range?.days
        ? api.get(`/api/flux/campaigns/admin/summary?dateFrom=${encodeURIComponent(daysAgoIso(range.days))}`)
        : Promise.resolve(null),
    ];
    Promise.allSettled(calls)
      .then(([a, b, c]) => {
        setLifetime(a.status === 'fulfilled' ? a.value : null);
        setToday(b.status === 'fulfilled' ? b.value : null);
        setRanged(c.status === 'fulfilled' ? c.value : null);
      })
      .finally(() => setStripLoading(false));
  }, [rangeId]);

  const loadActive = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (operator) q.set('operator', operator);
    api.get(`/api/flux/campaigns/admin/dashboard?${q.toString()}`)
      .then((body) => { setCampaigns(body?.campaigns || []); setTotal(body?.total || 0); setError(null); })
      .catch((err) => setError(err.body?.error || err.message))
      .finally(() => setLoading(false));
  }, [offset, operator]);

  const loadArchived = useCallback(() => {
    api.get(`/api/flux/campaigns/admin/archived?limit=${PAGE_SIZE}`)
      .then((body) => setArchived(body?.campaigns || []))
      .catch(() => { /* the active list is the page; archived is secondary */ });
  }, []);

  useEffect(() => { loadStrip(); }, [loadStrip]);
  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { loadArchived(); }, [loadArchived]);
  useEffect(() => { loadTransport(); }, [loadTransport]);

  useEffect(() => {
    api.get('/api/recruits/team-members')
      .then((body) => setOperators(body?.team_members || []))
      .catch(() => setOperators([]));
  }, []);

  // In-page only, and the RANK IS THE SERVER'S. A cross-page sort would be a
  // client ordering data it does not have (B-25's shape).
  const sorted = useMemo(() => [...campaigns].sort(bySortRankThenRecruit), [campaigns]);

  function togglePick(id) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function doUnarchive() {
    setConfirmOpen(false);
    setBusy(true);
    const ids = [...picked];
    const results = await Promise.allSettled(ids.map((id) => api.patch(`/api/flux/campaigns/${id}/unarchive`, {})));
    const failures = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        const c = archived.find((x) => x.id === ids[i]);
        failures.push(`${c?.name || ids[i]}: ${r.reason?.body?.error || r.reason?.message || 'failed'}`);
      }
    });
    const ok = ids.length - failures.length;
    setToast({
      tone: failures.length ? 'error' : 'success',
      // NAME the failures by campaign. "3 of 5" without saying which two is a
      // number the operator cannot act on.
      message: failures.length
        ? `Unarchived ${ok} of ${ids.length}. Failed — ${failures.join(' · ')}`
        : `Unarchived ${ok} of ${ids.length}. They return HARD-PAUSED — review and Resume from each campaign page.`,
    });
    setPicked(new Set());
    setBusy(false);
    loadActive();
    loadArchived();
    loadStrip();
  }

  const pages = Math.ceil(total / PAGE_SIZE);
  // S95 X-6: ONE binding, not two. This previously fed a `shownLabel` that
  // special-cased 'all' to 'Lifetime' while the pill rendered RANGES' 'All'.
  // RANGES now carries the word itself, so the pill and the tile band read the
  // same string from the same place and cannot drift apart again.
  const shownLabel = RANGES.find((r) => r.id === rangeId)?.label || '';

  // SQ-Chrome S1 — the picker now drives the tiles. It previously drove only a
  // text line below itself, which Jake removed, leaving an inert control.
  // ⚠ NO NEW REQUEST. `loadStrip` has always fetched the ranged summary on every
  // pick and discarded it; this renders what was already on the wire. Both
  // `lifetime` and `ranged` come from the SAME endpoint (/admin/summary), so
  // T-14's "includes archived" note holds for either.
  const shown = rangeId === 'all' ? lifetime : ranged;

  // ── S95 X-5 — THE EMERGENCY STOP'S THREE STATES, DERIVED ONCE ─────────────
  // The panel used to read TWO unrelated fields into one paragraph: a heading
  // branching on `stopped`/`valid`, and beneath it `transport?.reason || '…
  // dispatch fails closed.'` — a fallback that is not an error state at all,
  // just what renders when nobody typed a reason. So `OPEN` and "dispatch
  // fails closed" sat together on a money-adjacent control, with neither line
  // lying about its own field. Jake's rewrite deletes that line rather than
  // patching it, which removes the class instead of the instance.
  // 🔴 UNKNOWN IS RED, NOT INK. A gate we cannot read must never present as
  // the safe state — that is the same defect being fixed here, pointed the
  // other way.
  const gateValid = Boolean(transport?.valid);
  const gateStopped = Boolean(transport?.stopped);
  const gateLoud = !gateValid || gateStopped;
  const gate = !gateValid
    ? { word: 'UNKNOWN', subtitle: 'Cannot read system state.' }
    : gateStopped
      ? { word: 'ON', subtitle: 'System is NOT SENDING.' }
      : {
        word: 'OFF',
        subtitle: `System is sending since: ${transport?.activated_at ? new Date(transport.activated_at).toLocaleString() : '—'}`,
      };

  return (
    <div className="min-h-screen bg-[#F4F4F2]">
      {/* ── masthead. FluxEngine names itself, and — for the first time — there
          is a route back to the Front Office. Every surface under /campaigns
          previously dead-ended with no way home. ─────────────────────────── */}
      <header className="max-w-6xl mx-auto px-6 pt-8 pb-6">
        {/* S90 P12A item 11 — the page is named for what it is. */}
        <h1 className="font-display font-normal text-[#0E2A20] text-5xl leading-none tracking-tight">
          CCF FluxEngine
        </h1>
        {/* Backlink rides the subtitle's row, hard right — Jake, SQ-Chrome S1. */}
        <div className="mt-2 flex items-baseline justify-between gap-6 flex-wrap">
          <p className="text-sm text-[#5A5A5A]">
            Campaign Email Engine for Individual Recruit-to-Coach Outreach
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5A5A5A] hover:text-[#0E2A20] transition-colors whitespace-nowrap"
          >
            ← Front Office
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-16 space-y-6">

        {/* ── LIFETIME — the dashboard. Differentiated from every list below by
            being the one panel built out of tiles rather than rows. ───────── */}
        <section className={PANEL}>
          <div className="px-5 pt-5 pb-4 flex flex-col items-center text-center gap-1">
            <h2 className="font-display font-normal text-[#0E2A20] text-2xl leading-none">CCF FluxEngine Stat Dashboard</h2>
          </div>

          <div className={DASH} />

          {/* The panel title no longer carries the period, so the tile band
              names its own. Without this the four numerals are unlabelled and
              the picker below them gives no sign of WHICH period it just set.
              ⚠ CORRECTED S95: this read "looks like it drives them," written
              when the picker drove only a text line. It drives them for real
              now — see `shown` above — so the label names the period rather
              than compensating for a control that did nothing.
              T-14: /admin/dashboard EXCLUDES cancelled and this strip does not.
              The two numbers legitimately differ, so the strip says so. */}
          <div className="px-5 pb-3 flex flex-col items-center text-center gap-0.5">
            <span className="text-[0.69rem] font-semibold uppercase tracking-[0.18em] text-[#0E2A20]">{shownLabel}</span>
            <span className="text-xs text-[#5A5A5A]">All campaigns including archived.</span>
          </div>

          {stripLoading ? (
            <div className="px-5 py-6 text-sm text-[#5A5A5A] text-center">Loading…</div>
          ) : !shown ? (
            <div className="px-5 py-6 text-sm text-[#5A5A5A] text-center">—</div>
          ) : (
            <div className="flex flex-wrap divide-x-2 divide-dashed divide-[#0E2A20]">
              <Tile label="Sent" value={shown.sent} spark={0} />
              {/* open_rate / click_rate arrive PRE-FORMATTED from shapeStats.
                  Rendered as strings — formatting them again is how two pages
                  disagree. */}
              <Tile label="Opens" value={shown.opened} sub={shown.open_rate} spark={1} />
              <Tile label="Clicked" value={shown.clicked} sub={shown.click_rate} spark={2} />
              <Tile label="Bounced" value={shown.bounced} spark={3} />
            </div>
          )}

          <div className={DASH} />

          <div className="px-5 py-4 text-lg text-[#0E2A20] tabular-nums text-center">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[#5A5A5A] mr-3 align-middle">Today</span>
            {today ? `${today.sent} sent · ${today.opened} opens (${today.open_rate}) · ${today.clicked} clicked (${today.click_rate})` : '—'}
          </div>

          <div className={DASH} />

          <div className="px-5 py-4">
            <div className="flex items-center justify-center gap-2 flex-wrap mb-3">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRangeId(r.id)}
                  /* Board grammar: solid fill = active, DASHED outline = inactive. */
                  className={`text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 ${
                    rangeId === r.id
                      ? 'bg-[#0E2A20] text-white border-[#0E2A20]'
                      : 'border-dashed border-[#0E2A20] text-[#0E2A20] hover:bg-white'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {/* SQ-Chrome S1: the stats line that used to sit under this picker is gone —
                its numbers moved UP into the headline tiles, and the picker now drives
                those tiles directly (see `shown`, above). The `ranged` fetch has always
                run; nothing was added to the wire.
                ⚠ CORRECTED S95 (Jake). This comment previously said the picker "drives
                nothing the operator can see" and told the next reader to wire it or
                delete it. That was true for the minutes between the line being removed
                and the tiles being wired — the same session — and false by the time it
                was committed. */}
          </div>
        </section>

        {/* New Campaign sits UNDER the dashboard, not in the masthead — Jake,
            SQ-Chrome S1. The numbers are what you come to this page for; the
            action follows them. */}
        <div>
          <button type="button" onClick={() => navigate('/campaigns/new')} className={BTN_DARK}>
            New Campaign
          </button>
        </div>

        {/* ── active — 1×2 grid, reduced weight ─────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display font-normal text-[#0E2A20] text-2xl">Active</h2>
            <label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5A5A5A] flex items-center gap-2">
              Operator
              <select
                value={operator}
                onChange={(e) => { setOperator(e.target.value); setOffset(0); }}
                className="text-xs border-2 border-[#0E2A20] rounded-full px-3 py-1.5 text-[#0E2A20] bg-white normal-case tracking-normal"
              >
                <option value="">All</option>
                {operators.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
              </select>
            </label>
          </div>

          {error ? <p className="text-sm text-red-700">{error}</p> : null}
          {loading ? <p className="text-sm text-[#5A5A5A]">Loading…</p> : null}
          {!loading && !error && sorted.length === 0 ? (
            <p className="text-sm text-[#5A5A5A]">No campaigns yet.</p>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-fr">
            {sorted.map((c) => {
              const sd = c.status_display || {};
              const na = c.next_action || {};
              const eta = etaText(c.eta);
              return (
                /* The 14px left spine is the board's card signature. */
                <div
                  key={c.id}
                  className="bg-white rounded-2xl border-2 border-l-[14px] border-[#0E2A20] h-full flex flex-col"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/campaigns/${c.id}`)}
                    className="text-left w-full group p-4 flex-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-[0.55rem] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${severityClass(sd.severity)}`}>
                        {sd.badge || '—'}
                      </span>
                      {c.stats ? (
                        <span className="text-xs text-[#5A5A5A] ml-auto tabular-nums">
                          {c.stats.sent}/{c.stats.total} sent
                        </span>
                      ) : null}
                    </div>
                    <div className="font-display font-normal text-[#0E2A20] text-base leading-snug group-hover:text-mid-green transition-colors">
                      {c.recruitName || 'Unassigned'} | {c.name}
                    </div>
                    <p className="text-xs text-[#5A5A5A] mt-1 truncate">
                      {c.send_from_email}{c.operatorName ? ` · ${c.operatorName}` : ''}
                    </p>
                    <p className="text-xs text-[#0E2A20] mt-2">{sd.label}</p>
                    {na.text ? <p className="text-xs text-[#5A5A5A]">{na.text}</p> : null}
                    {eta ? <p className="text-[0.65rem] text-[#5A5A5A] mt-1">{eta}</p> : null}
                    {c.stats ? (
                      <p className="text-[0.65rem] text-[#5A5A5A] mt-1.5 tabular-nums">
                        {c.stats.opened} opens ({c.stats.open_rate}) · {c.stats.clicked} clicked ({c.stats.click_rate})
                      </p>
                    ) : null}
                  </button>

                  {/* ⚠ KEYED ON next_action.action, NEVER ON THE TOKEN CLASS.
                      An identity mismatch is TOKEN-class and its action is null
                      (§0.2 Q1) — keying on the class would offer a re-auth for a
                      wrong-mailbox pairing that re-authorizing cannot fix. */}
                  {na.action === 'reauth' ? (
                    <div className={`${DASH} p-4`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* W-12. This button now does what its label says.
                            Hidden entirely without oauth.invite, because the
                            server enforces the same capability and a control
                            that can only 403 is worse than no control (§1.1). */}
                        {canInvite ? (
                          <button
                            type="button"
                            onClick={() => sendReauth(c)}
                            disabled={reauthSending === c.id || !c.send_from_email}
                            className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 border-amber-500 text-amber-800 bg-amber-50 disabled:opacity-50"
                          >
                            {reauthSending === c.id ? 'Sending…' : 'Send Re-Auth Email'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setReauthFor(reauthFor === c.id ? null : c.id)}
                          className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border-2 border-dashed border-[#0E2A20] text-[#0E2A20]"
                        >
                          {reauthFor === c.id ? 'Hide options' : 'Other options'}
                        </button>
                      </div>
                      {/* 🔴 THIS COMMENT READ "P9b's send-invite is not built."
                          IT IS BUILT — `router.post('/send-invite', requireTeam,
                          requireCan('oauth.invite'))` at flux-oauth.js:314, on
                          origin/main and deployed. `OutreachTab.jsx:183` calls
                          it. Corrected S86; the claim had been false since P9b
                          landed and nothing here re-checked it.

                          ⚠ THE BEHAVIOUR BELOW IS DELIBERATELY UNCHANGED. This
                          card still opens W-11's connect controls rather than
                          calling send-invite — wiring it is a behaviour change,
                          not a comment fix, and it was not ordered here. It is
                          reported as a live gap: the button reads "Send Re-Auth
                          Email" and sends nothing.

                          ⚠ AND THE OPERATOR-FACING LINE BELOW IS STALE TOO —
                          "Emailing the link is P9b" tells a human something
                          untrue, which is worse than a stale comment. Left in
                          place only because changing user copy is a product
                          decision; flagged in the same report as the gap. */}
                      {reauthFor === c.id ? (
                        <div className="mt-3">
                          {/* W-13. This read "Emailing the link is P9b. Connect
                              or copy the link here in the meantime." P9b
                              shipped. A stale comment misleads a developer
                              eventually; stale operator copy misleads the
                              operator now, which is why it was worth its own
                              work item. */}
                          <p className="text-xs text-[#5A5A5A] mb-2">
                            Send Re-Auth Email mails the connect link to
                            {' '}
                            <strong>{c.send_from_email || 'the campaign mailbox'}</strong>
                            . Or connect the mailbox yourself below, or copy the link and send it another way.
                          </p>
                          <ConnectGmail recruitId={c.recruit_id} gmail={c.send_from_email} />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {pages > 1 ? (
            <div className="flex items-center gap-3 mt-4">
              <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}>
                Previous
              </button>
              <span className="text-xs text-[#5A5A5A] tabular-nums">
                {Math.floor(offset / PAGE_SIZE) + 1} of {pages} · {total} campaigns
              </span>
              <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}>
                Next
              </button>
            </div>
          ) : null}
        </section>

        {/* ── archived ──────────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display font-normal text-[#0E2A20] text-2xl">Archived</h2>
            {picked.size > 0 ? (
              <button type="button" disabled={busy} onClick={() => setConfirmOpen(true)} className={`${BTN_DARK} disabled:opacity-50`}>
                Unarchive Selected ({picked.size})
              </button>
            ) : null}
          </div>

          {archived.length === 0 ? (
            <p className="text-sm text-[#5A5A5A]">Nothing archived.</p>
          ) : (
            <div className={`${PANEL_QUIET} overflow-hidden`}>
              {archived.map((c, i) => (
                <label key={c.id} className={`flex items-center gap-3 px-5 py-3 cursor-pointer ${i > 0 ? DASH : ''}`}>
                  <input
                    type="checkbox"
                    checked={picked.has(c.id)}
                    onChange={() => togglePick(c.id)}
                    className="accent-mid-green"
                  />
                  <span className="text-sm text-[#0E2A20] flex-1">
                    {c.recruitName || 'Unassigned'} · {c.name}
                  </span>
                  <span className="text-xs text-[#5A5A5A] tabular-nums">
                    {c.stats ? `${c.stats.sent} sent / ${c.stats.opened} opens / ${c.stats.clicked} clicked` : '—'}
                  </span>
                </label>
              ))}
            </div>
          )}
        </section>

        {/* ── CAMPAIGN TRANSPORT — LAST ON THE PAGE, ON PURPOSE ────────────
            Jake, SQ-Chrome S1: "Move the Campaign Transport thing ALL THE WAY
            to the bottom. It's an emergency switch, it deserves no prominence."
            It used to sit above the stats, first thing an operator saw.
            ⚠ Nothing about its BEHAVIOUR moved — same endpoint, same
            expectedVersion optimistic-concurrency check, same required reason,
            same admin gate. Only its position and its weight changed.
            ⚠ S95 (Jake, X-5): the copy is rewritten and the loud treatment is
            RED, not amber. It shouts when STOPPED *and* when UNREADABLE; when
            it is OFF it is furniture. Jake: "Needs nothing else" — which is why
            the Version/first-activated line and the reason line are GONE, not
            reworded. Behaviour is untouched: same two endpoints, same
            expectedVersion check, same required reason, same admin gate, same
            disabled predicate. */}
        {isAdmin ? (
          <section
            className={`rounded-2xl border-2 p-5 ${
              gateLoud
                ? 'bg-red-50 border-red-600 shadow-[0_4px_0_#991B1B]'
                : 'bg-transparent border-dashed border-[#0E2A20]/40'
            }`}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className={`font-display font-normal ${gateLoud ? 'text-red-700 text-xl' : 'text-[#0E2A20] text-sm uppercase tracking-[0.18em] font-semibold'}`}>
                  EMERGENCY SYSTEM STOP: {gate.word}
                </h2>
                <p className={`text-sm mt-1 ${gateLoud ? 'text-red-700' : 'text-[#5A5A5A]'}`}>{gate.subtitle}</p>
              </div>
              <div className="flex gap-2 flex-wrap items-start">
                <input
                  value={transportReason}
                  onChange={(e) => setTransportReason(e.target.value)}
                  maxLength={500}
                  placeholder="Reason for STOPPING."
                  className="text-sm border-2 border-[#0E2A20]/40 rounded-full px-4 py-2 bg-white"
                />
                <button
                  type="button"
                  disabled={transportBusy || !transport?.valid || !transportReason.trim()}
                  onClick={() => changeTransport(!transport.stopped)}
                  className={`${transport?.stopped ? BTN_DARK : BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`}
                >
                  {/* ⚠ Dynamic on purpose: a button reading EMERGENCY STOP that
                      turns sending ON is a live hazard. */}
                  {transport?.stopped ? 'RESUME SENDING' : 'EMERGENCY STOP'}
                </button>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <ConfirmModal
        open={confirmOpen}
        title="Unarchive Campaigns"
        body={`Unarchive ${picked.size} campaign${picked.size === 1 ? '' : 's'}? They return to the active dashboard HARD-PAUSED — review each one and Resume it from its campaign page.`}
        confirmLabel="Unarchive"
        onConfirm={doUnarchive}
        onCancel={() => setConfirmOpen(false)}
      />
      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}
